/**
 * Vertically centers a carousel's arrows on the first slide's media instead of
 * the whole card, so they line up with the image whatever the image ratio,
 * column count or text below it.
 *
 * Wrap a slideshow with <media-centered-arrows>. It measures the first slide's
 * media and sets `--arrow-top` (px from the top of <slideshow-arrows>), then
 * re-measures whenever the carousel or that media resizes.
 */
class MediaCenteredArrows extends HTMLElement {
  /** Media inside a card, in order of preference. */
  static MEDIA_SELECTOR = '.card-gallery, img, video, svg.placeholder-svg, .placeholder-svg';

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {Element | null} */
  #media = null;

  connectedCallback() {
    this.#observer = new ResizeObserver(() => this.#update());
    this.#observer.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#media = null;
  }

  #update = () => {
    // Direct children only: product cards contain their own nested image slideshows
    const container = this.querySelector(':scope > slideshow-component > slideshow-container');
    const arrows = container?.querySelector(':scope > slideshow-arrows');
    const media = container
      ?.querySelector(':scope > slideshow-slides > slideshow-slide')
      ?.querySelector(MediaCenteredArrows.MEDIA_SELECTOR);
    if (!(arrows instanceof HTMLElement) || !media) return;

    // Track the media itself too: images can resize without the carousel changing width
    if (media !== this.#media) {
      if (this.#media) this.#observer?.unobserve(this.#media);
      this.#media = media;
      this.#observer?.observe(media);
    }

    const mediaRect = media.getBoundingClientRect();
    if (mediaRect.height === 0) return;

    const top = mediaRect.top + mediaRect.height / 2 - arrows.getBoundingClientRect().top;
    arrows.style.setProperty('--arrow-top', `${Math.round(top)}px`);
    arrows.classList.add('slideshow-arrows--media-centered');
  };
}

if (!customElements.get('media-centered-arrows')) {
  customElements.define('media-centered-arrows', MediaCenteredArrows);
}
